import { useCallback, useMemo } from 'react';

import { Queries } from '@aline/common';
import type { Core } from '@pdftron/webviewer';
import type { Path } from 'slate';
import { comparePDFAnnotationPosition, useAreAnnotationsLoaded } from 'src/components';
import { getPDFCheckboxSelectedAnswerIdsFromOptionFields } from 'src/components/viewer/hooks/crud';
import { useMaybeDocumentViewer } from 'src/components/viewer/viewer-context';
import {
  useEditorTabStateSelectorForTabId,
  useGetFieldsInJSON,
} from 'src/editor/editor-tab-state-registry';
import { useGetCurrentDoc } from 'src/hooks';

type CompareInputField = { nodeId?: UUID };
type FieldPositionsByNodeId = Record<UUID, { locations: Path[] }>;
export type PDFField = Core.Annotations.Forms.Field;
export type PDFFieldsByNodeId = Map<UUID, PDFField>;

export function useGetCompareFieldsByDocPosition(tabId: UUID | null): {
  sortFieldsByDocPosition: (a: CompareInputField, b: CompareInputField) => number;
} {
  const { document } = useGetCurrentDoc();
  const { sortPDFFieldsByDocPosition } = useGetComparePDFFieldsByDocPosition(tabId);
  const { sortAlineFieldsByDocPosition } = useGetCompareAlineFieldsByDocPosition();
  const sortFieldsByDocPosition = useCallback(
    (a: CompareInputField, b: CompareInputField): number => {
      switch (document?.latestVersion.__typename) {
        case 'DocumentVersion':
          return sortAlineFieldsByDocPosition(a, b);
        case 'StaticFileVersion':
          return sortPDFFieldsByDocPosition(a, b);
        default:
          return 0;
      }
    },
    [document?.latestVersion.__typename, sortAlineFieldsByDocPosition, sortPDFFieldsByDocPosition]
  );

  return { sortFieldsByDocPosition };
}

/**
 * Hook that returns a compare function that can be used to sort fields in the current document by position.
 */
export function useGetCompareAlineFieldsByDocPosition(): {
  sortAlineFieldsByDocPosition: (a: CompareInputField, b: CompareInputField) => number;
} {
  const {
    fieldsInJSON: { cannedInChildren, inChildren },
  } = useGetFieldsInJSON();
  const positionsByNodeId = useMemo(
    () => ({ ...inChildren, ...cannedInChildren }),
    [cannedInChildren, inChildren]
  );
  const sortAlineFieldsByDocPosition = useCallback(
    (a: CompareInputField, b: CompareInputField): number => {
      const compareFunc = getCompareFieldsBySlatePosition(positionsByNodeId);
      return compareFunc(a, b);
    },
    [positionsByNodeId]
  );

  return { sortAlineFieldsByDocPosition };
}

/** Compare Slate input and canned fields by their earliest document placement. */
export function getCompareFieldsBySlatePosition(
  inChildren: FieldPositionsByNodeId
): (a: CompareInputField, b: CompareInputField) => number {
  return (inputFieldA, inputFieldB) => {
    const fieldADetails = inputFieldA.nodeId ? inChildren[inputFieldA.nodeId] : null;
    const fieldBDetails = inputFieldB.nodeId ? inChildren[inputFieldB.nodeId] : null;
    if (!fieldADetails || !fieldBDetails) {
      const fieldANum = fieldADetails ? 1 : 0;
      const fieldBNum = fieldBDetails ? 1 : 0;

      // Sort A before B if A occurs in the document and B does not, and vice versa.
      return fieldBNum - fieldANum;
    }

    const { locations: fieldALocations } = fieldADetails;
    const { locations: fieldBLocations } = fieldBDetails;
    const [fieldAEarliestLocation = null] = [...fieldALocations].sort(Queries.comparePaths);
    const [fieldBEarliestLocation = null] = [...fieldBLocations].sort(Queries.comparePaths);
    if (!fieldAEarliestLocation || !fieldBEarliestLocation) return 0;

    return Queries.comparePaths(fieldAEarliestLocation, fieldBEarliestLocation);
  };
}

/**
 * Hook that returns a compare function that can be used to sort PDF fields in the current document by position.
 */
export function useGetComparePDFFieldsByDocPosition(tabId: UUID | null): {
  pdfFieldsByNodeId: PDFFieldsByNodeId;
  sortPDFFieldsByDocPosition: (a: CompareInputField, b: CompareInputField) => number;
} {
  const pdfFieldsByNodeId = useGetPDFFieldsByNodeId(tabId);
  const sortPDFFieldsByDocPosition = useCallback(
    (a: CompareInputField, b: CompareInputField): number => {
      const compareFunc = getComparePDFFieldPosition(pdfFieldsByNodeId);
      return compareFunc(a, b);
    },
    [pdfFieldsByNodeId]
  );

  return { pdfFieldsByNodeId, sortPDFFieldsByDocPosition };
}

export function useGetPDFFieldsByNodeId(tabId: UUID | null): PDFFieldsByNodeId {
  const pdfFields = useGetPDFFields(tabId);

  return useMemo(() => {
    const checkboxOptionFieldsByVariableNodeId = new Map<UUID, Core.Annotations.Forms.Field[]>();
    const pdfFieldsByNodeId: PDFFieldsByNodeId = new Map();

    for (const field of pdfFields) {
      const parsed = Queries.parseCheckboxOptionNodeId(field.name);
      if (parsed) {
        const optionFields = checkboxOptionFieldsByVariableNodeId.get(parsed.variableNodeId) ?? [];
        optionFields.push(field);
        checkboxOptionFieldsByVariableNodeId.set(parsed.variableNodeId, optionFields);
        continue;
      }

      pdfFieldsByNodeId.set(field.name, field);
    }

    for (const [variableNodeId, optionFields] of checkboxOptionFieldsByVariableNodeId) {
      pdfFieldsByNodeId.set(
        variableNodeId,
        createCheckboxVariablePDFField(variableNodeId, optionFields)
      );
    }

    return pdfFieldsByNodeId;
  }, [pdfFields]);
}

function createCheckboxVariablePDFField(
  variableNodeId: UUID,
  optionFields: Core.Annotations.Forms.Field[]
): Core.Annotations.Forms.Field {
  const [primaryField] = [...optionFields].sort((fieldA, fieldB) => {
    const [widgetA = null] = [...(fieldA.widgets ?? [])].sort(comparePDFAnnotationPosition);
    const [widgetB = null] = [...(fieldB.widgets ?? [])].sort(comparePDFAnnotationPosition);
    if (!widgetA) return 1;
    if (!widgetB) return -1;
    return comparePDFAnnotationPosition(widgetA, widgetB);
  });

  const allWidgets = optionFields.flatMap(field => field.widgets ?? []);

  const partialField: Partial<Core.Annotations.Forms.Field> = {
    getValue: () => getPDFCheckboxSelectedAnswerIdsFromOptionFields(optionFields).join(','),
    name: variableNodeId,
    widgets: allWidgets,
  };

  /**
   * Since Core.Annotations.Forms.Field includes prototype methods, TypeScript (correctly) won't let
   * us just shallow clone it using spreading. To make this type safe, we also need to copy the
   * prototype of primaryField onto field.
   */
  const field = { ...primaryField, ...partialField } as typeof primaryField;
  Object.setPrototypeOf(field, Object.getPrototypeOf(primaryField));
  return field;
}

/**
 * Get fields in the PDF, ensuring they are kept up-to-date as they change in the PDF.
 */
export function useGetPDFFields(tabId: UUID | null): Core.Annotations.Forms.Field[] {
  const documentViewer = useMaybeDocumentViewer();
  const annotationsLoaded = useAreAnnotationsLoaded();
  const annotationManager = documentViewer?.getAnnotationManager();
  const fieldManager = annotationManager?.getFieldManager();
  const annotationsChangedCount = useEditorTabStateSelectorForTabId(
    tabId,
    state => state.viewer.annotationsChangedCount
  );

  return useMemo(() => {
    if (!annotationsLoaded || !fieldManager) return [];
    /**
     * 1.  Using annotationsChangedCount is a hack to ensure we refetch pdfFields when annotationsChangedCount changes.
     * 2.  Spread fields into new array [...] b/c Apryse uses the same array under the hood, and we want consumers of this hook to fire when fields change.
     */
    return annotationsChangedCount ? [...fieldManager.getFields()] : [...fieldManager.getFields()];
  }, [annotationsChangedCount, annotationsLoaded, fieldManager]);
}

export function useGetIsPDFFieldInDocument(tabId: UUID | null): {
  getIsPDFFieldInDoc: (nodeId: UUID) => boolean;
} {
  const pdfFields = useGetPDFFields(tabId);
  const getIsPDFFieldInDoc = useCallback(
    (nodeId: UUID) => {
      return pdfFields.some(pdfField => {
        if (!pdfField.widgets?.length) return false;
        const name = pdfField.name;
        if (name === nodeId) return true;
        const parsed = Queries.parseCheckboxOptionNodeId(name);
        return parsed?.variableNodeId === nodeId;
      });
    },
    [pdfFields]
  );

  return { getIsPDFFieldInDoc };
}

export function getComparePDFFieldPosition(
  pdfFieldsByNodeId: PDFFieldsByNodeId
): (fieldA: CompareInputField, fieldB: CompareInputField) => number {
  return (fieldA, fieldB) => {
    const pdfFieldA = fieldA.nodeId ? pdfFieldsByNodeId.get(fieldA.nodeId) : null;
    const pdfFieldB = fieldB.nodeId ? pdfFieldsByNodeId.get(fieldB.nodeId) : null;
    if (!pdfFieldA || !pdfFieldB) {
      const fieldANum = pdfFieldA ? 1 : 0;
      const fieldBNum = pdfFieldB ? 1 : 0;
      return fieldBNum - fieldANum;
    }

    const fieldAWidgets = pdfFieldA.widgets ?? [];
    const fieldBWidgets = pdfFieldB.widgets ?? [];
    const [earliestAnnotationA = null] = [...fieldAWidgets].sort(comparePDFAnnotationPosition);
    const [earliestAnnotationB = null] = [...fieldBWidgets].sort(comparePDFAnnotationPosition);
    if (!earliestAnnotationA || !earliestAnnotationB) {
      const annotANum = earliestAnnotationA ? 1 : 0;
      const annotBNum = earliestAnnotationB ? 1 : 0;
      return annotBNum - annotANum;
    }

    return comparePDFAnnotationPosition(earliestAnnotationA, earliestAnnotationB);
  };
}
